import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { notify } from '../../helpers/utils';
import { MapPin, Phone, User, Calendar, AlertCircle, Search, Edit2, Trash2, ShoppingCart, Loader } from 'lucide-react';

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  surf2:   'var(--nd-surface-2)',
  surf3:   'var(--nd-surface-2)',
  border:  'var(--nd-w08)',
  bord2:   'var(--nd-w10)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx35)',
  faint:   'var(--nd-w10)',
  blue:    '#3B82F6',
  teal:    '#22D3A5',
  amber:   '#FBBF24',
  red:     '#F87171',
  purple:  '#A78BFA',
  green:   '#34D399',
  font:    "'DM Sans', sans-serif",
  mono:    "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

  .sc-input {
    width: 100%; padding: 9px 12px 9px 36px;
    background: var(--nd-w05); border: 1px solid var(--nd-w10);
    border-radius: 9px; color: var(--nd-text);
    font-size: 13px; font-family: 'DM Sans', sans-serif;
    outline: none; transition: border-color 0.15s, background 0.15s;
  }
  .sc-input::placeholder { color: var(--nd-tx25); }
  .sc-input:focus { border-color: rgba(59,130,246,0.55); background: rgba(59,130,246,0.06); }

  .sc-search-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 9px 18px; border: none; border-radius: 9px;
    background: linear-gradient(135deg,#3B82F6,#22D3A5); color: var(--nd-text-strong);
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    cursor: pointer; white-space: nowrap;
    box-shadow: 0 4px 14px rgba(59,130,246,0.28); transition: opacity 0.15s;
  }
  .sc-search-btn:hover { opacity: 0.88; }

  .sc-card { animation: fadeUp 0.3s ease both; }
  .sc-card:hover { box-shadow: 0 10px 32px rgba(0,0,0,0.45) !important; transform: translateY(-2px); transition: all 0.2s; }

  .sc-action-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 6px 12px; border-radius: 7px; font-size: 11px; font-weight: 700;
    font-family: 'DM Sans', sans-serif; cursor: pointer; transition: all 0.15s; border: 1px solid;
  }
  .sc-btn-create { background: rgba(34,211,165,0.10); color: #22D3A5; border-color: rgba(34,211,165,0.25); }
  .sc-btn-create:hover { background: rgba(34,211,165,0.18); border-color: rgba(34,211,165,0.45); }
  .sc-btn-edit   { background: rgba(59,130,246,0.10); color: #60A5FA;  border-color: rgba(59,130,246,0.25); }
  .sc-btn-edit:hover   { background: rgba(59,130,246,0.18); border-color: rgba(59,130,246,0.45); }
  .sc-btn-delete { background: rgba(248,113,113,0.10); color: #F87171; border-color: rgba(248,113,113,0.25); }
  .sc-btn-delete:hover { background: rgba(248,113,113,0.18); border-color: rgba(248,113,113,0.45); }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--nd-w10); border-radius: 4px; }
`;

// ── Priority badge ─────────────────────────────────────────────────
const PriorityBadge = ({ priority }) => {
  const p = (priority || '').toString().toLowerCase();
  const cfg = {
    high:   { color: '#F87171', bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.28)' },
    medium: { color: '#FBBF24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.28)'  },
    low:    { color: '#34D399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.28)'  },
  }[p] || { color: 'var(--nd-tx35)', bg: 'var(--nd-w05)', border: 'var(--nd-w12)' };

  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px', borderRadius: 5,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'capitalize',
      fontFamily: T.mono, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
    }}>
      {priority || 'N/A'}
    </span>
  );
};

// ── Info row inside a customer card ──────────────────────────────
const InfoRow = ({ Icon, iconColor, label, value, children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 7 }}>
    <div style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 1,
      background: `${iconColor}14`, border: `1px solid ${iconColor}28`,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={11} color={iconColor} />
    </div>
    <div style={{ fontSize: 12, color: T.muted, minWidth: 72 }}>{label}</div>
    <div style={{ fontSize: 12, color: T.text, fontWeight: 600, flex: 1 }}>
      {children || value || 'N/A'}
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════
//   MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const SearchCustomer = () => {
  const [customers, setCustomers]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [searchPhone, setSearchPhone] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [searchName, setSearchName]   = useState('');
  const debounceRef = useRef(null);
  const navigate    = useNavigate();

  const fetchCustomers = async (search = '', name = '') => {
    if (!search.trim() && !name.trim()) return;
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/customers`, {
        params: { phone: search.trim(), name: name.trim() },
      });
      if (res.data && res.data.data) {
        setCustomers(res.data.data);
        setHasSearched(true);
      } else {
        notify('error', 'Invalid API response');
      }
    } catch (err) {
      console.error('API Error:', err);
      notify('error', 'Failed to fetch customer');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!searchPhone && !searchName) {
      setCustomers([]);
      setHasSearched(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCustomers(searchPhone, searchName);
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [searchPhone, searchName]);

  const handleDelete = async (custId) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/customers/${custId}`);
        fetchCustomers(searchPhone, searchName);
      } catch (err) {
        notify('error', 'Failed to delete customer');
        console.error('Delete Error:', err);
      }
    }
  };

  // ── Initials avatar ─────────────────────────────────────────────
  const getInitials = (name = '') => name.trim().split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
  const avatarGradients = ['#667eea,#764ba2','#4facfe,#00f2fe','#43e97b,#38f9d7','#fa709a,#fee140','#30cfd0,var(--nd-surface)','#f093fb,#f5576c'];
  const getGradient = (name = '') => {
    const hash = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return avatarGradients[hash % avatarGradients.length];
  };

  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: '14px 20px' }}>
      <style>{STYLES}</style>

      {/* ── PAGE HEADER ─────────────────────────────────────────── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 18, overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px' }}>
          <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 5 }}>iNOVAA · SALES</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>Search Customer</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>Find customers by phone number or name</div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {/* ── SEARCH BAR ──────────────────────────────────────────── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px 18px', marginBottom: 18 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Phone input */}
          <div style={{ position: 'relative', flex: '1 1 180px', minWidth: 160 }}>
            <Phone size={13} color={T.muted} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              className="sc-input"
              type="text"
              placeholder="Search by phone number"
              value={searchPhone}
              onChange={e => setSearchPhone(e.target.value)}
            />
          </div>

          {/* Name input */}
          <div style={{ position: 'relative', flex: '1 1 180px', minWidth: 160 }}>
            <User size={13} color={T.muted} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              className="sc-input"
              type="text"
              placeholder="Search by name"
              value={searchName}
              onChange={e => setSearchName(e.target.value)}
            />
          </div>

          <button
            className="sc-search-btn"
            onClick={() => fetchCustomers(searchPhone, searchName)}
          >
            <Search size={14} />
            Search
          </button>
        </div>
      </div>

      {/* ── LOADING ─────────────────────────────────────────────── */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '52px 20px', gap: 12 }}>
          <Loader size={20} color={T.blue} style={{ animation: 'spin 0.8s linear infinite' }} />
          <span style={{ fontSize: 13, color: T.muted }}>Searching customers…</span>
        </div>
      )}

      {/* ── RESULTS ─────────────────────────────────────────────── */}
      {!loading && hasSearched && (
        <div>
          {/* result count */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <User size={14} color={T.blue} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Customer Information</span>
            <span style={{ fontSize: 10, color: T.muted, fontFamily: T.mono, marginLeft: 2 }}>
              {customers.length} {customers.length === 1 ? 'result' : 'results'}
            </span>
          </div>

          {customers.length === 0 ? (
            <div style={{
              background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
              padding: '52px 20px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 46, marginBottom: 14 }}>🔍</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>No customers found</div>
              <div style={{ fontSize: 12, color: T.muted }}>Try a different phone number or name</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
              {customers.map((customer, i) => {
                const gradient = getGradient(customer.client_name || '');
                return (
                  <div key={customer.id} className="sc-card" style={{
                    background: T.surface, border: `1px solid ${T.border}`,
                    borderRadius: 14, overflow: 'hidden',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                    animationDelay: `${i * 0.05}s`,
                  }}>
                    {/* Card top strip */}
                    <div style={{ height: 3, background: `linear-gradient(90deg,${gradient.split(',')[0]},${gradient.split(',')[1]})` }} />

                    <div style={{ padding: '16px 18px' }}>
                      {/* Avatar + name row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                        <div style={{
                          width: 44, height: 44, borderRadius: 11, flexShrink: 0,
                          background: `linear-gradient(135deg,${gradient})`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'var(--nd-text-strong)', fontWeight: 800, fontSize: 16,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.30)',
                        }}>
                          {getInitials(customer.client_name)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: 15, color: T.text, marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {customer.client_name}
                          </div>
                          <div style={{ fontSize: 10, color: T.muted, fontFamily: T.mono }}>
                            ID: {customer.Cust_id}
                          </div>
                        </div>
                        <PriorityBadge priority={customer.priority} />
                      </div>

                      {/* Info rows */}
                      <div style={{ marginBottom: 14 }}>
                        <InfoRow Icon={Phone}    iconColor={T.teal}   label="Phone"    value={customer.phone_number} />
                        <InfoRow Icon={MapPin}   iconColor={T.blue}   label="City"     value={customer.client_city} />
                        <InfoRow Icon={MapPin}   iconColor={T.purple} label="Address"  value={customer.address} />
                        <InfoRow Icon={Calendar} iconColor={T.amber}  label="Added On" value={new Date(customer.date_added).toLocaleDateString()} />
                        <InfoRow Icon={AlertCircle} iconColor={T.blue} label="Pin">
                          {customer.pin_location ? (
                            <a
                              href={customer.pin_location}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: T.blue, textDecoration: 'none', fontSize: 12, fontWeight: 600 }}
                            >
                              <MapPin size={12} /> Open in Maps
                            </a>
                          ) : 'N/A'}
                        </InfoRow>
                      </div>

                      {/* Divider */}
                      <div style={{ height: 1, background: T.border, marginBottom: 14 }} />

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                        <button
                          className="sc-action-btn sc-btn-create"
                          onClick={() => navigate(`/sales/sales-order/create/${customer.Cust_id}`)}
                        >
                          <ShoppingCart size={11} /> Create Order
                        </button>
                        <button
                          className="sc-action-btn sc-btn-edit"
                          onClick={() => navigate(`/sales/edit-customer/${customer.Cust_id}`)}
                        >
                          <Edit2 size={11} /> Edit
                        </button>
                        <button
                          className="sc-action-btn sc-btn-delete"
                          onClick={() => handleDelete(customer.Cust_id)}
                        >
                          <Trash2 size={11} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── EMPTY STATE (before search) ─────────────────────────── */}
      {!loading && !hasSearched && (
        <div style={{ textAlign: 'center', padding: '64px 20px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16, margin: '0 auto 18px',
            background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Search size={28} color="rgba(59,130,246,0.5)" />
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 6 }}>Search to find a customer</div>
          <div style={{ fontSize: 12, color: T.muted }}>Enter a phone number or name above to get started</div>
        </div>
      )}
    </div>
  );
};

export default SearchCustomer;